package xim.poc.gl

import org.khronos.webgl.WebGLRenderingContext.Companion.COLOR_BUFFER_BIT
import org.khronos.webgl.WebGLRenderingContext.Companion.DEPTH_BUFFER_BIT
import org.khronos.webgl.WebGLRenderingContext.Companion.STENCIL_BUFFER_BIT
import xim.math.Matrix4f
import xim.math.Vector3f
import xim.poc.*
import xim.poc.camera.CameraReference
import xim.poc.camera.StaticCamera
import xim.poc.game.ActorStateManager
import xim.resource.DatId
import xim.resource.FogParams
import xim.resource.LightingParams
import xim.resource.TextureLink
import xim.util.PI_f
import xim.util.toRads
import kotlin.math.min

private class TransientBuffer(val buffer: GLScreenBuffer, var currentId: Int = 0)

private data class PhotoRequest(val actorId: ActorId, val transient: Boolean)

object PortraitBufferManager {

    private const val maxBuffers = 32

    private val availableBuffers = ArrayList<GLScreenBuffer>()
    private val claimedBuffers = HashMap<Int, GLScreenBuffer>()
    private lateinit var transientBuffer: TransientBuffer

    private val photoRequests = HashMap<ActorId, PhotoRequest>()

    private var initialized = false

    fun setup() {
        if (initialized) { return }
        initialized = true

        val webgl = GlDisplay.getContext()
        for (i in 0 until maxBuffers) {
            availableBuffers += GLScreenBuffer(webgl = webgl, name = "portrait    $i", width = 64, height = 64)
        }

        transientBuffer = TransientBuffer(GLScreenBuffer(webgl = webgl, name = "portrait    tran", width = 64, height = 64))
    }

    fun isReady(): Boolean {
        return initialized && availableBuffers.all { it.isReady() } && transientBuffer.buffer.isReady()
    }

    fun takePhotoIfNeeded(actor: Actor, drawXimCommand: DrawXimCommand) {
        if (!isReady()) { return }

        val photoRequest = photoRequests[actor.id] ?: return
        photoRequests.remove(actor.id)

        val modelLook = actor.state.getBaseLook()
        if (claimedBuffers.containsKey(modelLook.modelId)) { return }

        if (photoRequest.transient) {
            if (transientBuffer.currentId == modelLook.modelId) { return }
            transientBuffer.currentId = modelLook.modelId
            takePhoto(modelLook, actor, drawXimCommand, transientBuffer.buffer)
            return
        }

        val claimed = availableBuffers.removeFirstOrNull() ?: return
        claimedBuffers[modelLook.modelId] = claimed
        takePhoto(modelLook, actor, drawXimCommand, claimed)
    }

    fun releaseClaimedBuffers() {
        if (!isReady()) { return }

        availableBuffers.addAll(claimedBuffers.values)
        claimedBuffers.clear()
    }

    fun submitPhotoRequest(actorId: ActorId, transient: Boolean = false) {
        photoRequests[actorId] = PhotoRequest(actorId, transient)
    }

    fun getTakenPhoto(actorId: ActorId): TextureLink? {
        if (!isReady()) { return null }

        val actorState = ActorStateManager[actorId] ?: return null
        val modelLook = actorState.getBaseLook()

        val current = claimedBuffers[modelLook.modelId]
        if (current != null) { return current.texture }

        if (transientBuffer.currentId == modelLook.modelId) { return transientBuffer.buffer.texture }

        return null
    }

    private fun takePhoto(modelLook: ModelLook, actor: Actor, drawCommand: DrawXimCommand, portraitBuffer: GLScreenBuffer) {
        val model = actor.actorModel?.model ?: return
        val customSettings = portraitSettings[modelLook.modelId]

        val fakeModel = ActorModel(actor, model)
        fakeModel.setSkeletonAnimation(DatId("idl0"), actor.getAllAnimationDirectories(), transitionParams = TransitionParams(transitionInTime = 0f))
        fakeModel.update(0.1f)

        val fakeSkeleton = fakeModel.getSkeleton() ?: return
        fakeSkeleton.animate(actor, fakeModel, null)

        val actorFacePosition = fakeSkeleton.getStandardJointPosition(6)
        val actorFacingDirection = Matrix4f().rotateYInPlace(actor.displayFacingDir + PI_f/8 + (customSettings?.angleOffset ?: 0f)).transform(Vector3f.X)

        val boundingBox = actor.getSkeletonBoundingBox(0)

        val distance = if (customSettings?.distance != null) {
            customSettings.distance
        } else if (boundingBox == null) {
            0.5f
        } else {
            val size = 0.25f * min(boundingBox.width(), boundingBox.height())
            size.coerceIn(0.35f, 2f)
        }

        val cameraPosition = (customSettings?.cameraOffset ?: Vector3f.ZERO) + actorFacePosition + actorFacingDirection * distance
        val camera = StaticCamera(position = cameraPosition, viewDir = actorFacingDirection, fov = (customSettings?.fov ?: 45f).toRads())

        FrameBufferManager.bindAndClearMiscBuffer()
        MainTool.drawer.setupXimSkinned(camera)
        MainTool.drawer.drawXimSkinned(drawCommand.copy(
            translate = Vector3f.ZERO,
            rotation = Vector3f.ZERO,
            skeleton = fakeSkeleton,
            effectColor = Color(ByteColor.half),
            wrapEffect = null,
            lightingParams = LightingParams(
                ambientColor = customSettings?.ambientColor ?: Color(0.65f, 0.65f, 0.65f, 1f),
                fog = FogParams.noOpFog,
                lights = emptyList()
            ),
        ))

        bindAndClear(portraitBuffer)
        MainTool.drawer.drawScreenBuffer(sourceBuffer = FrameBufferManager.getMiscBuffer(), destBuffer = portraitBuffer)

        FrameBufferManager.bindScreenBuffer()
        MainTool.drawer.setupXimSkinned(CameraReference.getInstance())
    }

    private fun bindAndClear(frameBuffer: GLScreenBuffer) {
        frameBuffer.bind()

        val webgl = GlDisplay.getContext()
        webgl.clearColor(0f, 0f, 0f, 0f)
        webgl.clear(COLOR_BUFFER_BIT or DEPTH_BUFFER_BIT or STENCIL_BUFFER_BIT)
    }

}

private class PortraitSettings(val distance: Float? = null, val ambientColor: Color? = null, val angleOffset: Float? = null, val cameraOffset: Vector3f? = null, val fov: Float? = null)

private val portraitSettings = mapOf(
    0x104 to PortraitSettings(distance = 0.2f),
    0x6BB to PortraitSettings(distance = 0.3f, angleOffset = PI_f/16f),
    0x6D0 to PortraitSettings(distance = 0.3f, angleOffset = PI_f/16f),
    0x7EE to PortraitSettings(ambientColor = Color(0.28f, 0.28f, 0.28f, 1f)),
    0xB41 to PortraitSettings(distance = 0.27f, angleOffset = -PI_f/8f, cameraOffset = Vector3f(0f, -0.05f, 0f), fov = 35f),
)